package songwritersnotebook.app.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.PlaylistAdd
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.SavedChord
import songwritersnotebook.app.ui.theme.BlueAccent
import songwritersnotebook.app.ui.theme.GoldAccent
import songwritersnotebook.app.ui.theme.SliderThumbGray
import songwritersnotebook.app.ui.theme.SliderTrackGray
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.tutorial.tutorialTarget
import org.jetbrains.compose.resources.painterResource
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_guitar
import androidx.compose.foundation.interaction.MutableInteractionSource

@OptIn(ExperimentalLayoutApi::class, ExperimentalMaterial3Api::class)
@Composable
fun ChordPanel(
    orderedChordNames: List<String>,
    chordLibrary: Map<String, ChordDefinition>,
    favoriteChords: List<SavedChord>,
    selectedChord: String?,
    onChordClick: (String) -> Unit,
    onAddChordClick: () -> Unit,
    onEditChord: () -> Unit,
    chordsInUse: Set<String>,
    onDeleteChord: (String) -> Unit,
    selectedInstrument: String,
    onInstrumentChange: (String) -> Unit,
    strumSpeed: Float,
    onStrumSpeedChange: (Float) -> Unit,
    selectedFavorite: SavedChord? = null,
    onSelectFavorite: (SavedChord?) -> Unit = {},
    onAddFavoriteToSong: (SavedChord) -> Unit = {},
    onRemoveFavorite: (SavedChord) -> Unit = {},
    onEditFavorite: (SavedChord) -> Unit = {},
    onPlayFavorite: (SavedChord) -> Unit = {},
    onAddNewFavorite: () -> Unit = {},
    favoriteProgressions: List<songwritersnotebook.app.data.SavedProgression> = emptyList(),
    selectedFavoriteProgression: songwritersnotebook.app.data.SavedProgression? = null,
    onSelectFavoriteProgression: (songwritersnotebook.app.data.SavedProgression?) -> Unit = {},
    onAddFavoriteProgressionToSong: (songwritersnotebook.app.data.SavedProgression) -> Unit = {},
    onRemoveFavoriteProgression: (songwritersnotebook.app.data.SavedProgression) -> Unit = {},
    onEditFavoriteProgression: (songwritersnotebook.app.data.SavedProgression) -> Unit = {},
    onPlayFavoriteProgression: (songwritersnotebook.app.data.SavedProgression) -> Unit = {},
    progressions: List<songwritersnotebook.app.data.ChordProgression> = emptyList(),
    activeProgressionId: String? = null,
    activeProgressionIndex: Int = 0,
    onProgressionClick: (String) -> Unit = {},
    onAddProgressionClick: () -> Unit = {},
    onEditProgressionClick: (String) -> Unit = {},
    onDeleteProgressionClick: (String) -> Unit = {},
    onToggleFavoriteProgression: (songwritersnotebook.app.data.ChordProgression) -> Unit = {}
) {
    LaunchedEffect(Unit) {
        println("DEBUG_UI: ChordPanel Initializing - Chords: ${orderedChordNames.size}, LibSize: ${chordLibrary.size}, StrumSpeed: $strumSpeed")
    }

    var isStrumSliderVisible by remember { mutableStateOf(false) }
    var showProgressions by remember { mutableStateOf(false) }
    var showFavorites by remember { mutableStateOf(false) }

    val currentTutorialStep by songwritersnotebook.app.tutorial.TutorialManager.currentStep.collectAsState()
    LaunchedEffect(currentTutorialStep) {
        if (currentTutorialStep == songwritersnotebook.app.tutorial.TutorialStep.EDITOR_PROGRESSION_BUILDER) {
            showProgressions = true
            showFavorites = false
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surface)
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                "Chord Library",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f)
            )

            TextButton(onClick = { isStrumSliderVisible = !isStrumSliderVisible }) {
                Text("Strum")
            }

            Row(
                modifier = Modifier
                    .height(32.dp)
                    .border(
                        width = 1.dp,
                        color = MaterialTheme.colorScheme.outline,
                        shape = RoundedCornerShape(50)
                    ),
                verticalAlignment = Alignment.CenterVertically
            ) {
                val pianoColors = if (selectedInstrument == "piano") ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
                val guitarColors = if (selectedInstrument == "guitar") ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)

                Button(
                    onClick = { onInstrumentChange("piano") },
                    shape = RoundedCornerShape(topStartPercent = 50, bottomStartPercent = 50),
                    modifier = Modifier.fillMaxHeight().width(48.dp),
                    contentPadding = PaddingValues(0.dp),
                    colors = pianoColors
                ) {
                    Icon(Icons.Filled.Piano, contentDescription = "Piano", modifier = Modifier.size(18.dp))
                }

                Box(modifier = Modifier.fillMaxHeight().width(1.dp).background(MaterialTheme.colorScheme.outline))

                Button(
                    onClick = { onInstrumentChange("guitar") },
                    shape = RoundedCornerShape(topEndPercent = 50, bottomEndPercent = 50),
                    modifier = Modifier.fillMaxHeight().width(48.dp),
                    contentPadding = PaddingValues(0.dp),
                    colors = guitarColors
                ) {
                    Icon(painter = painterResource(Res.drawable.ic_guitar), contentDescription = "Guitar", modifier = Modifier.size(18.dp))
                }
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 16.dp, bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            SingleChoiceSegmentedButtonRow {
                SegmentedButton(
                    shape = SegmentedButtonDefaults.itemShape(index = 0, count = 2),
                    onClick = { showProgressions = false },
                    selected = !showProgressions
                ) {
                    Text("Chords")
                }
                SegmentedButton(
                    shape = SegmentedButtonDefaults.itemShape(index = 1, count = 2),
                    onClick = { showProgressions = true },
                    selected = showProgressions,
                    modifier = Modifier.tutorialTarget(TutorialStep.EDITOR_CHORD_LIBRARY_PROGRESSION_TAB)
                ) {
                    Text("Progressions")
                }
            }

            IconToggleButton(
                checked = showFavorites,
                onCheckedChange = { showFavorites = it }
            ) {
                Icon(
                    imageVector = if (showFavorites) Icons.Default.Favorite else Icons.Default.FavoriteBorder,
                    contentDescription = "Favorites",
                    tint = if (showFavorites) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        AnimatedVisibility(visible = isStrumSliderVisible) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    "Strum Speed",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Slider(
                    value = if (strumSpeed.isNaN()) 40f else strumSpeed,
                    onValueChange = { onStrumSpeedChange(it) },
                    valueRange = 0f..120f,
                    modifier = Modifier.weight(1f),
                    colors = SliderDefaults.colors(
                        thumbColor = SliderThumbGray,
                        activeTrackColor = SliderTrackGray,
                        inactiveTrackColor = SliderTrackGray.copy(alpha = 0.5f)
                    ),
                    thumb = {
                        SliderDefaults.Thumb(
                            interactionSource = remember { MutableInteractionSource() },
                            thumbSize = DpSize(12.dp, 12.dp),
                            colors = SliderDefaults.colors(thumbColor = SliderThumbGray)
                        )
                    }
                )
                IconButton(onClick = { isStrumSliderVisible = false }) {
                    Icon(Icons.Default.Check, contentDescription = "Confirm Strum Speed")
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        val scrollState = rememberScrollState()
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(max = 220.dp) // Expanded height
                .verticalScroll(scrollState)
                .padding(vertical = 12.dp)
        ) {
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                if (!showProgressions && !showFavorites) {
                    orderedChordNames.forEach { chordName ->
                        val isSelected = chordName == selectedChord
                        val isInUse = chordsInUse.contains(chordName)
                        val containerColor = if (isInUse) GoldAccent else MaterialTheme.colorScheme.primary
                        val contentColor = if (isInUse) Color.Black else MaterialTheme.colorScheme.onPrimary
                        val borderColor = if (isSelected) {
                            if (isInUse) BlueAccent else GoldAccent
                        } else {
                            Color.Transparent
                        }

                        OutlinedButton(
                            onClick = { onChordClick(chordName) },
                            modifier = if (chordName == "Dm") Modifier.tutorialTarget(TutorialStep.EDITOR_CHORD_LIBRARY_SELECT) else Modifier,
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                            border = BorderStroke(width = 2.dp, color = borderColor),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = containerColor,
                                contentColor = contentColor
                            )
                        ) {
                            Text(chordName, fontFamily = FontFamily.Monospace)
                        }

                        if (isSelected) {
                            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                OutlinedButton(
                                    onClick = onEditChord,
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp).tutorialTarget(TutorialStep.EDITOR_OPEN_CREATOR_EXPLAIN)
                                ) {
                                    Icon(Icons.Default.Edit, contentDescription = "Edit Chord")
                                }
                                OutlinedButton(
                                    onClick = { onDeleteChord(chordName) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.5f))
                                ) {
                                    Icon(Icons.Default.Delete, contentDescription = "Delete Chord")
                                }
                            }
                        }
                    }

                    if (selectedChord == null) {
                        OutlinedButton(
                            onClick = onAddChordClick,
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(0.dp),
                            modifier = Modifier.size(40.dp).tutorialTarget(TutorialStep.EDITOR_OPEN_CREATOR_EXPLAIN)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = "Add new Chord")
                        }
                    }
                } else if (!showProgressions && showFavorites) {
                    // Favorites Tab
                    favoriteChords.forEach { fav ->
                        val isSelected = fav.id == selectedFavorite?.id
                        val containerColor = if (isSelected) MaterialTheme.colorScheme.secondary else MaterialTheme.colorScheme.surfaceVariant
                        val contentColor = if (isSelected) MaterialTheme.colorScheme.onSecondary else MaterialTheme.colorScheme.onSurfaceVariant
                        
                        OutlinedButton(
                            onClick = { 
                                if (isSelected) {
                                    onSelectFavorite(null)
                                } else {
                                    onPlayFavorite(fav)
                                    onSelectFavorite(fav) 
                                }
                            },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = containerColor,
                                contentColor = contentColor
                            ),
                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, if (isSelected) BlueAccent else Color.Transparent)
                        ) {
                            Text(fav.chord.name, fontFamily = FontFamily.Monospace)
                        }

                        if (isSelected) {
                            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                OutlinedButton(
                                    onClick = { onAddFavoriteToSong(fav) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp)
                                ) {
                                    Icon(Icons.AutoMirrored.Filled.PlaylistAdd, contentDescription = "Add to Song")
                                }
                                OutlinedButton(
                                    onClick = { onEditFavorite(fav) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp)
                                ) {
                                    Icon(Icons.Default.Edit, contentDescription = "Edit Favorite")
                                }
                                OutlinedButton(
                                    onClick = { onRemoveFavorite(fav) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.5f))
                                ) {
                                    Icon(Icons.Default.Delete, contentDescription = "Remove Favorite")
                                }
                            }
                        }
                    }

                    if (selectedFavorite == null) {
                        OutlinedButton(
                            onClick = onAddNewFavorite,
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(0.dp),
                            modifier = Modifier.size(40.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = "Add new Favorite")
                        }
                        
                        if (favoriteChords.isEmpty()) {
                            Text("No favorite chords yet.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.padding(start = 8.dp).align(Alignment.CenterVertically))
                        }
                    }
                } else if (showProgressions && !showFavorites) {
                    // Song Progressions
                    progressions.forEach { prog ->
                        val isSelected = prog.id == activeProgressionId
                        val containerColor = MaterialTheme.colorScheme.primary
                        val contentColor = MaterialTheme.colorScheme.onPrimary
                        val borderColor = if (isSelected) GoldAccent else Color.Transparent

                        val displayString = "[${prog.name}: " + prog.chordNames.mapIndexed { index, chord ->
                            if (isSelected && index == activeProgressionIndex) ">$chord<" else chord
                        }.joinToString(" - ") + "]"
                        
                        OutlinedButton(
                            onClick = { onProgressionClick(prog.id) },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = containerColor,
                                contentColor = contentColor
                            ),
                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, borderColor)
                        ) {
                            Text(displayString, fontFamily = FontFamily.Monospace)
                        }

                        if (isSelected) {
                            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                OutlinedButton(
                                    onClick = { onEditProgressionClick(prog.id) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp)
                                ) {
                                    Icon(Icons.Default.Edit, contentDescription = "Edit Progression")
                                }
                                OutlinedButton(
                                    onClick = { onDeleteProgressionClick(prog.id) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.5f))
                                ) {
                                    Icon(Icons.Default.Delete, contentDescription = "Delete Progression")
                                }
                            }
                        }
                    }
                    if (activeProgressionId == null) {
                        OutlinedButton(
                            onClick = onAddProgressionClick,
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(0.dp),
                            modifier = Modifier.size(40.dp).tutorialTarget(TutorialStep.EDITOR_PROGRESSION_BUILDER)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = "Add new Progression")
                        }
                    }
                } else if (showProgressions && showFavorites) {
                    // Favorite Progressions Tab
                    favoriteProgressions.forEach { favProg ->
                        val isSelected = favProg.id == selectedFavoriteProgression?.id
                        val containerColor = if (isSelected) MaterialTheme.colorScheme.secondary else MaterialTheme.colorScheme.surfaceVariant
                        val contentColor = if (isSelected) MaterialTheme.colorScheme.onSecondary else MaterialTheme.colorScheme.onSurfaceVariant
                        
                        val displayString = "[${favProg.progression.name}: " + favProg.progression.chordNames.joinToString(" - ") + "]"
                        
                        OutlinedButton(
                            onClick = { 
                                if (isSelected) {
                                    onSelectFavoriteProgression(null)
                                } else {
                                    onPlayFavoriteProgression(favProg)
                                    onSelectFavoriteProgression(favProg)
                                }
                            },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = containerColor,
                                contentColor = contentColor
                            ),
                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, if (isSelected) BlueAccent else Color.Transparent)
                        ) {
                            Text(displayString, fontFamily = FontFamily.Monospace)
                        }

                        if (isSelected) {
                            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                OutlinedButton(
                                    onClick = { onAddFavoriteProgressionToSong(favProg) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp)
                                ) {
                                    Icon(Icons.AutoMirrored.Filled.PlaylistAdd, contentDescription = "Add to Song")
                                }
                                OutlinedButton(
                                    onClick = { onEditFavoriteProgression(favProg) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp)
                                ) {
                                    Icon(Icons.Default.Edit, contentDescription = "Edit Favorite Progression")
                                }
                                OutlinedButton(
                                    onClick = { onRemoveFavoriteProgression(favProg) },
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(0.dp),
                                    modifier = Modifier.size(40.dp),
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.5f))
                                ) {
                                    Icon(Icons.Default.Delete, contentDescription = "Remove Favorite Progression")
                                }
                            }
                        }
                    }

                    if (favoriteProgressions.isEmpty()) {
                        Text("No favorite progressions yet.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.padding(start = 8.dp).align(Alignment.CenterVertically))
                    }
                }
            }
        }
    }
}
